// vc-savelater.js - Mode-aware version
(function () {
  const LOG = false;
  const log = (...a) => LOG && console.log('[SaveLater]', ...a);

  const LS_KEY = 'vc_saved_items';

  // -------------------------
  // Mode Helpers
  // -------------------------
  function getCurrentMode() {
    // Priority: body.dataset.mode (set by inline script immediately)
    // This is more reliable than ModeManager which may be stale
    if (document.body.dataset.mode) {
      return document.body.dataset.mode;
    }
    if (window.ModeManager && typeof window.ModeManager.getCurrentMode === 'function') {
      return window.ModeManager.getCurrentMode();
    }
    return 'shop';
  }

  function getItemMode(item) {
    return item.mode || 'shop'; // Default to 'shop' for legacy items
  }

  // -------------------------
  // Helpers
  // -------------------------
  function safeParse(str, fallback) {
    try {
      const v = JSON.parse(str);
      return Array.isArray(v) ? v : fallback;
    } catch {
      return fallback;
    }
  }

  function mergeSavedArrays(a, b) {
    const map = new Map();
    // Process both arrays - later items (from b) will overwrite earlier ones (from a)
    [...a, ...b].forEach((item) => {
      if (!item || !item.variantId) return;
      const id = String(item.variantId);
      const qty = Number(item.quantity || item.qty || 1);
      // Always use the LATEST quantity (overwrite, don't add)
      map.set(id, {
        ...item,
        variantId: id,
        quantity: qty,
      });
    });
    return Array.from(map.values());
  }

  function loadGuest() {
    return safeParse(localStorage.getItem(LS_KEY), []);
  }
  function saveGuest(arr) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(arr));
    } catch (_) {}
  }
  function clearGuest() {
    try {
      localStorage.removeItem(LS_KEY);
    } catch (_) {}
  }

  // -------------------------
  // Initial state: server + guest
  // -------------------------
  let serverSaved = [];

  try {
    if (window.VC_SAVED_SERVER) {
      if (Array.isArray(window.VC_SAVED_SERVER)) {
        serverSaved = window.VC_SAVED_SERVER;
      } else if (typeof window.VC_SAVED_SERVER === 'string') {
        serverSaved = JSON.parse(window.VC_SAVED_SERVER);
      }
    }
  } catch (e) {
    console.warn('[SaveLater] failed to parse server list', e);
    serverSaved = [];
  }

  log('Server saved items:', serverSaved.length, serverSaved);

  const guestSaved = loadGuest();
  log('Guest saved items:', guestSaved.length, guestSaved);

  if (window.VC_CUSTOMER_ID) {
    // Logged in: Use server data as source of truth
    // Only merge guest items if they don't exist in server data
    if (guestSaved.length > 0) {
      // Find items in guest that are NOT in server (by variantId)
      const serverVariantIds = new Set(serverSaved.map(i => String(i.variantId || i.variant_id)));
      const newGuestItems = guestSaved.filter(item => {
        const vid = String(item.variantId || item.variant_id);
        return !serverVariantIds.has(vid);
      });
      
      if (newGuestItems.length > 0) {
        log('Found new guest items to merge:', newGuestItems.length);
        window.VC_SAVED = [...serverSaved, ...newGuestItems];
        
        (async () => {
          await syncSavedToServer();
          clearGuest();
          log('Merged new guest saved items into metafield');
          renderSavedList();
        })();
      } else {
        // All guest items already in server, just clear localStorage
        log('All guest items already in server, clearing localStorage');
        window.VC_SAVED = serverSaved;
        clearGuest();
        renderSavedList();
      }
    } else {
      // No guest items, just use server data
      window.VC_SAVED = serverSaved;
      renderSavedList();
    }
  } else {
    // Guest: only local
    window.VC_SAVED = guestSaved;
    renderSavedList();
  }
  
  log('Final VC_SAVED:', window.VC_SAVED?.length, window.VC_SAVED);

  // -------------------------
  // Sync helpers
  // -------------------------
  async function syncSavedToServer() {
    // Always keep local copy (for quick reload in same browser)
    saveGuest(window.VC_SAVED || []);

    if (!window.VC_CUSTOMER_ID) return; // not logged in: local-only

    try {
      await fetch(`${window.VEDHACART_CONFIG.serverUrl}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: window.VC_CUSTOMER_ID,
          items: window.VC_SAVED || [],
        }),
      });
    } catch (e) {
      console.warn('[SaveLater] sync to server failed', e);
    }
  }

  // -------------------------
  // Filter items by mode
  // -------------------------
  function getItemsForMode(mode) {
    const items = window.VC_SAVED || [];
    return items.filter(item => {
      const itemMode = getItemMode(item);
      return itemMode === mode;
    });
  }

  function getInStockItemsForMode(mode) {
    return getItemsForMode(mode).filter(item => {
      const isOutOfStock = item.outOfStock === true || item.outOfStock === 'true';
      return !isOutOfStock;
    });
  }

  function getOutOfStockItemsForMode(mode) {
    return getItemsForMode(mode).filter(item => {
      const isOutOfStock = item.outOfStock === true || item.outOfStock === 'true';
      return isOutOfStock;
    });
  }

  // -------------------------
  // Update Saved count button (mode-aware)
  // -------------------------
  function updateSavedCount() {
    const currentMode = getCurrentMode();
    const items = window.VC_SAVED || [];
    
    // Filter by current mode first
    const modeItems = items.filter(item => getItemMode(item) === currentMode);
    
    // Only count in-stock items (exclude outOfStock items)
    const inStockItems = modeItems.filter(item => {
      const isOutOfStock = item.outOfStock === true || item.outOfStock === 'true';
      return !isOutOfStock;
    });
    const count = inStockItems.length;
    
    // Count out of stock items for current mode
    const outOfStockItems = modeItems.filter(item => {
      const isOutOfStock = item.outOfStock === true || item.outOfStock === 'true';
      return isOutOfStock;
    });
    const outOfStockCount = outOfStockItems.length;
    
    log('updateSavedCount - mode:', currentMode, 'total items:', items.length, 'mode items:', modeItems.length, 'in-stock:', count, 'out-of-stock:', outOfStockCount);
    
    // Update all saved count elements (shows only current mode's in-stock count)
    document.querySelectorAll('#vc-saved-count').forEach(el => {
      el.textContent = String(count);
    });
    
    // Show/hide the saved button based on count for CURRENT MODE only
    const totalModeCount = modeItems.length;
    document.querySelectorAll('#vc-saved-btn').forEach(btn => {
      if (totalModeCount > 0) {
        btn.hidden = false;
        log('Showing saved button - mode:', currentMode, 'count:', totalModeCount);
      } else {
        btn.hidden = true;
        log('Hiding saved button - mode:', currentMode, 'no items');
      }
    });

    // Update cart page tab counts
    const savedTabCount = document.getElementById('saved-tab-count');
    if (savedTabCount) {
      savedTabCount.textContent = String(count);
    }

    const outOfStockTabCount = document.getElementById('outofstock-tab-count');
    const outOfStockTab = document.getElementById('outofstock-tab');
    if (outOfStockTabCount) {
      outOfStockTabCount.textContent = String(outOfStockCount);
    }
    if (outOfStockTab) {
      outOfStockTab.hidden = outOfStockCount === 0;
    }
  }

  // Alias for backward compatibility
  function renderSavedList() {
    updateSavedCount();
  }

  // Make renderer available to vc-cartbox.js
  window.VC_renderSavedList = updateSavedCount;

  // -------------------------
  // Core mutations
  // -------------------------
  function upsertSavedFromPayload(payload, shouldAdd = true) {
    const vid = String(payload.variantId || payload.variant_id);
    const qty = Number(payload.quantity || payload.qty || 1);
    const mode = payload.mode || getCurrentMode(); // Use payload mode or current mode

    log('upsertSavedFromPayload - vid:', vid, 'qty:', qty, 'mode:', mode);

    const existingIndex = (window.VC_SAVED || []).findIndex(
      (i) => String(i.variantId || i.variant_id) === vid,
    );

    if (existingIndex !== -1) {
      // Update existing
      if (shouldAdd) {
        // When saving from cart, ADD to existing quantity
        window.VC_SAVED[existingIndex].quantity = 
          Number(window.VC_SAVED[existingIndex].quantity || 0) + qty;
        log('Added to existing saved item, new qty:', window.VC_SAVED[existingIndex].quantity);
      } else {
        // When setting (e.g., from merge), REPLACE quantity
        window.VC_SAVED[existingIndex].quantity = qty;
      }
      // Update mode in case item was saved without mode before
      window.VC_SAVED[existingIndex].mode = mode;
      log('Updated existing item mode to:', mode);
    } else {
      // Add new item with mode
      window.VC_SAVED.unshift({
        variantId: vid,
        title: payload.title || '',
        url: payload.url || '#',
        image: payload.image || '',
        variant_title: payload.variant_title || '',
        price: payload.price || '',
        quantity: qty,
        mode: mode, // Store the mode
        outOfStock: payload.outOfStock || false,
      });
      log('Added new saved item with mode:', mode);
    }
  }

  function removeSavedByVariantId(variantId) {
    const vid = String(variantId);
    window.VC_SAVED = (window.VC_SAVED || []).filter(
      (i) => String(i.variantId || i.variant_id) !== vid,
    );
  }

  // -------------------------
  // Actions
  // -------------------------
  async function handleSaveForLater(btn) {
    // Prevent double execution
    if (btn.dataset.processing === 'true') {
      log('Save for later already processing, skipping');
      return;
    }
    btn.dataset.processing = 'true';
    
    // Support both cartbox and cart page row classes
    const row = btn.closest('.vc-cartbox__row') || btn.closest('.vc-cart__item');
    const variantId = btn.dataset.variantId;
    const line = btn.dataset.line || (row ? row.dataset.line : null);
    const qty = Number(btn.dataset.qty || 1);
    
    // Get current mode at the moment of saving
    const saveMode = getCurrentMode();

    log('Save for later - variantId:', variantId, 'qty:', qty, 'line:', line, 'MODE:', saveMode);

    const payload = {
      variantId: variantId,
      title: btn.dataset.title || '',
      url: btn.dataset.url || '#',
      image: btn.dataset.image || '',
      variant_title: btn.dataset.variantTitle || '',
      price: btn.dataset.price || '',
      quantity: qty,
      mode: saveMode, // Explicitly set mode
    };

    log('Payload to save with mode:', payload.mode, payload);
    
    upsertSavedFromPayload(payload);
    log('After upsert, VC_SAVED:', JSON.stringify(window.VC_SAVED));
    
    await syncSavedToServer();
    renderSavedList();
    log('Saved items after render:', window.VC_SAVED);

    // Remove from cart using line number (more reliable for Shopify)
    if (line) {
      try {
        log('Removing from cart, line:', line);
        
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            line: parseInt(line, 10),
            quantity: 0
          }),
        });
        const cart = await response.json();
        
        log('Cart updated:', cart);
        
        // Update header cart count
        updateHeaderCartCount(cart.item_count);
        
        document.dispatchEvent(new CustomEvent('vc:cart:updated', { detail: cart }));
      } catch (e) {
        console.error('[SaveLater] Failed to remove from cart:', e);
      }
    }
    
    btn.dataset.processing = 'false';
  }
  
  // Update header cart icon count (matches product-quickview.js approach)
  function updateHeaderCartCount(count) {
    const selectors = ['[data-cart-count]', '#cart-count', '.cart-count', '.header-cart-count', '.cart-badge'];
    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        el.textContent = String(count);
        if (el.setAttribute) {
          el.setAttribute('aria-label', `Cart items: ${count}`);
        }
        el.hidden = false;
      });
    });
    log('Updated header cart count to:', count);
  }

  async function handleMoveToCart(btn) {
    const variantId = btn.dataset.variantId;
    const qty = Number(btn.dataset.qty || 1);
    if (!variantId || qty <= 0) return;

    const body = new URLSearchParams({
      id: String(variantId),
      quantity: String(qty),
    }).toString();

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type':
          'application/x-www-form-urlencoded; charset=UTF-8',
      },
      credentials: 'same-origin',
      body,
    }).then((r) => r.json());

    removeSavedByVariantId(variantId);
    await syncSavedToServer();
    renderSavedList();
    document.dispatchEvent(new Event('vc:cart:updated'));
  }

  async function handleUnsave(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    removeSavedByVariantId(variantId);
    await syncSavedToServer();
    renderSavedList();
  }

  // Remove item from cart (not saved list)
  async function handleRemoveFromCart(btn) {
    const line = btn.dataset.line;
    const row = btn.closest('.vc-cartbox__row') || btn.closest('.vc-cart__item');
    const lineNum = line || (row ? row.dataset.line : null);
    
    if (!lineNum) {
      log('No line number found for remove');
      return;
    }

    try {
      log('Removing from cart, line:', lineNum);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          line: parseInt(lineNum, 10),
          quantity: 0
        }),
      });
      const cart = await response.json();
      
      log('Removed from cart:', cart);
      
      // Update header cart count
      updateHeaderCartCount(cart.item_count);
      
      document.dispatchEvent(new CustomEvent('vc:cart:updated', { detail: cart }));
    } catch (e) {
      console.error('[SaveLater] Failed to remove from cart:', e);
    }
  }

  // -------------------------
  // Delegated events
  // -------------------------
  document.addEventListener('click', async (event) => {
    // Save for later: support data-vc-save and data-vc-save-for-later
    const btnSave = event.target.closest(
      '[data-vc-save],[data-vc-save-for-later]',
    );
    if (btnSave) {
      event.preventDefault();
      await handleSaveForLater(btnSave);
      return;
    }

    const btnMove = event.target.closest('[data-vc-move-cart]');
    if (btnMove) {
      event.preventDefault();
      await handleMoveToCart(btnMove);
      return;
    }

    const btnUnsave = event.target.closest('[data-vc-unsave]');
    if (btnUnsave) {
      event.preventDefault();
      await handleUnsave(btnUnsave);
      return;
    }

    // Remove from cart (not saved list)
    const btnRemoveCart = event.target.closest('[data-vc-remove-cart]');
    if (btnRemoveCart) {
      event.preventDefault();
      await handleRemoveFromCart(btnRemoveCart);
      return;
    }
  });

  // -------------------------
  // Listen for mode changes to update counts
  // -------------------------
  document.addEventListener('veda:mode:changed', function(e) {
    const newMode = e.detail ? e.detail.mode : getCurrentMode();
    log('Mode changed event received - new mode:', newMode);
    log('VC_SAVED items:', window.VC_SAVED?.length, window.VC_SAVED?.map(i => ({ id: i.variantId, mode: i.mode })));
    
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      renderSavedList();
    }, 50);
  });

  document.addEventListener('DOMContentLoaded', renderSavedList);

  // -------------------------
  // Expose helpers for other scripts
  // -------------------------
  window.vcSyncSavedToServer      = syncSavedToServer;
  window.vcRemoveSavedByVariantId = removeSavedByVariantId;
  window.VC_renderSavedList       = updateSavedCount;
  
  // Expose mode-aware getters
  window.vcGetSavedForMode        = getItemsForMode;
  window.vcGetInStockForMode      = getInStockItemsForMode;
  window.vcGetOutOfStockForMode   = getOutOfStockItemsForMode;
  window.vcGetCurrentMode         = getCurrentMode;
  
})();
